import React, { useState, useEffect, useRef } from 'react';
import { Award } from 'lucide-react';

const Riders = ({ riders }) => {
  return (
    <section id="riders" className="py-20 bg-black relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-0 w-72 h-72 bg-cyan-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-0 w-72 h-72 bg-yellow-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
            I Nostri <span className="text-cyan-400">Piloti</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-yellow-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Talento, velocità e determinazione: i protagonisti del nostro team
          </p>
        </div>

        {/* Riders Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {riders.map((rider, index) => (
            <RiderCard key={rider.id} rider={rider} />
          ))}
        </div>
      </div>
    </section>
  );
};

const RiderCard = ({ rider }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    races: 0,
    podiums: 0
  });
  const statsRef = useRef(null);

  useEffect(() => {
    if (!rider.stats) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [isVisible, rider.stats]);

  useEffect(() => {
    if (isVisible && rider.stats) {
      const duration = 1200; // 1.2 seconds
      const steps = 60;
      const stepDuration = duration / steps;

      const targets = {
        races: parseInt(rider.stats.races),
        podiums: parseInt(rider.stats.podiums)
      };

      let currentStep = 0;

      const interval = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        setCounters({
          races: Math.floor(targets.races * progress),
          podiums: Math.floor(targets.podiums * progress)
        });

        if (currentStep >= steps) {
          setCounters(targets);
          clearInterval(interval);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [isVisible, rider.stats]);

  return (
    <div className="group relative bg-gradient-to-br from-gray-900 to-black rounded-3xl overflow-hidden border border-gray-800 hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:scale-[1.02]">
      {/* Rider Image */}
      <div className="relative h-96 overflow-hidden">
        <img loading="lazy" decoding="async"
          src={rider.image}
          alt={rider.name}
          className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
          style={{ objectPosition: 'center 30%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        
        {/* Rider Number Overlay */}
        <div className="absolute top-6 right-6">
          <div className="text-8xl font-black text-white/20 group-hover:text-cyan-500/30 transition-colors duration-300">
            #{rider.number}
          </div>
        </div>
      </div>

      {/* Rider Info */}
      <div className="p-8">
        {/* Name and Category */}
        <div className="mb-6">
          <h3 className="text-3xl md:text-4xl font-black text-white mb-2">
            {rider.name}
          </h3>
          <div className="flex items-center gap-4 mb-4">
            <span className="px-4 py-1 bg-cyan-500 text-black font-bold text-sm rounded-full">
              #{rider.number}
            </span>
            <span className="px-4 py-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold text-sm rounded-full">
              {rider.category}
            </span>
            <span className="text-2xl">{rider.flag}</span>
          </div>
          <p className="text-gray-400 leading-relaxed">{rider.bio}</p>
        </div>

        {/* Stats - Only for riders with stats */}
        {rider.stats && (
          <div ref={statsRef} className="grid grid-cols-3 gap-4 mb-6 p-6 bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-xl border border-gray-700/50">
            <div className="text-center">
              <div className="text-3xl font-black text-cyan-400 mb-1">
                {counters.races}
              </div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Gare</div>
            </div>
            <div className="text-center border-l border-r border-gray-700">
              <div className="text-3xl font-black text-yellow-400 mb-1">
                {counters.podiums}
              </div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Podi</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-black text-white mb-1">
                {rider.stats.bestPosition}
              </div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Miglior Pos.</div>
            </div>
          </div>
        )}

        {/* Achievements - Only for riders with achievements */}
        {rider.achievements && (
          <div>
            <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              Achievements
            </h4>
            <ul className="space-y-2">
              {rider.achievements.map((achievement, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-3 text-gray-400"
                >
                  <Award className="w-4 h-4 text-cyan-400 mt-1 flex-shrink-0" />
                  <span className="text-sm">{achievement}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Riders;
